// pages/ProductAdmin.jsx
import React, { useState, useEffect } from 'react';
import ProductView from '../components/ProductView';
import axios from "axios";
import config from '../config/config';
import { X } from 'lucide-react';
const ProductAdmin = () => {
  const [activeDesign, setActiveDesign] = useState('design1');
  const [devMode, setDevMode] = useState(false);
  const [background, setBackground] = useState('');
  const [textColor, setTextColor] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const token = localStorage.getItem("token");

  // Load settings from DB
  useEffect(() => {
    const loadSettings = async () => {
      if (!token) return setLoading(false);

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const res = await axios.get(config.API_URL + '/api/pullProductViewSettings', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        const db = res.data.productSettings || {};

        setActiveDesign(db.activeDesign || 'design1');
        setDevMode(db.devMode || false);

        const styles = db.customStyles || {};
        setBackground(styles.background || '');
        setTextColor(styles.textColor || '');
      } catch (err) {
        console.error("Failed to load product settings:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [token]);

  // Current config for preview & save
  const currentConfig = {
    activeDesign,
    devMode,
    customStyles: { background, textColor },
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrf = document.cookie
        .split('; ')
        .find(r => r.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      await axios.post(
        config.API_URL + "/api/saveProductViewSettings",
        { settings: currentConfig },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrf || ''),
          },
          withCredentials: true,
        }
      );

      alert("Product view settings saved successfully! 🎉");
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save settings. Check console.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-xl text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Main Editor Area */}
      <div className="flex-1 p-8">
        <h1 className="text-3xl font-bold mb-8">Product View Editor</h1>

        {/* Design Selector */}
        <div className="mb-10">
          <h2 className="text-xl font-semibold mb-6">Choose Layout Design</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { value: 'design1', label: 'Classic Two-Column', desc: 'Balanced image + info layout' },
              { value: 'design2', label: 'Sticky Sidebar', desc: 'Persistent purchase panel' },
              { value: 'design3', label: 'Minimal Single Column', desc: 'Clean, focused, editorial style' },
            ].map((d) => (
              <button
                key={d.value}
                onClick={() => setActiveDesign(d.value)}
                className={`p-6 border-2 rounded-xl text-left transition-all ${
                  activeDesign === d.value
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow'
                }`}
              >
                <h3 className="font-medium text-lg">{d.label}</h3>
                <p className="text-sm text-gray-600 mt-1">{d.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Dev Mode - Custom Styling */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-yellow-900">Advanced Styling (Dev Mode)</h3>
            <button
              onClick={() => setDevMode(!devMode)}
              className={`w-11 h-6 rounded-full relative transition-colors ${
                devMode ? 'bg-yellow-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-all ${
                  devMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {devMode && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                <input
                  type="text"
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  placeholder="e.g. #ffffff or linear-gradient(...)"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  placeholder="e.g. #1f2937"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setPreviewOpen(true)}
            className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-md"
          >
            Preview Selected Design
          </button>

          <button
            onClick={saveSettings}
            disabled={saving}
            className={`px-8 py-4 rounded-xl font-semibold text-white transition-colors shadow-md ${
              saving ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>

      {/* Live Preview Overlay */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-auto pt-10">
          <div className="bg-white w-full max-w-7xl rounded-xl shadow-2xl overflow-hidden relative">
            {/* Close Button */}
            <button
              onClick={() => setPreviewOpen(false)}
              className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Preview Content */}
            <ProductView config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAdmin;